import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Select } from '@components/common';
import { cachedEndpointsService, SignupService } from '@services';
import utils from '@utils';

const validationSchema = Yup.object().shape({
	LocationTypeId : Yup.string().trim().required().label('Business Type'),
	StreetAddress  : Yup.string().trim().required().label('Address'),
	City           : Yup.string().trim().required().label('City'),
	StateId        : Yup.number().integer().required().label('State'),
	ZipCode        : Yup.number().required().label('Zip').typeError('Zip must be a valid number'),
});

const locationValidation = {
	LocationName : Yup.string().trim().required().label('Business Name'),
};

class BusinessInfo extends Component {
	constructor(props) {
		super(props);

		this.state = {
			loadingLocationTypes : false,
			locationTypes        : [],
			loading              : false,
		};

		const { user } = props;

		this.initialValues = {
			LocationName   : '',
			LocationTypeId : '',
			StreetAddress  : user.StreetAddress || '',
			City           : user.City || '',
			StateId        : user.StateId || '',
			ZipCode        : user.Zip || '',
		};
	}

	componentDidMount() {
		this.getLocationTypes();
	}

	getLocationTypes = () => {
		this.setState({ loadingLocationTypes : true });
		cachedEndpointsService.getLocationTypes()
			.then(({ data }) => {
				const locationTypes = data.map(({ Id : value, Name : label }) => ({ value, label }));
				this.setState({ locationTypes, loadingLocationTypes : false });
			})
			.catch(() => {
				this.setState({ loadingLocationTypes : false });
				utils.notify({
					type    : 'error',
					message : 'Error loading US States',
					title   : 'Oops',
				});
			});
	}

	updateAddress = (SignUpToken, StreetAddress, City, StateId, Zip) => (
		new Promise((resolve, reject) => {
			SignupService.attachAddress(SignUpToken, {
				StreetAddress,
				City,
				StateId,
				Zip,
			}).then(({ data : user, status : addressStatus }) => resolve({ user, addressStatus }))
				.catch(() => reject());
		})
	)

	handleSubmit = (values) => {
		const {
			StreetAddress, City, StateId, ZipCode,
		} = values;

		const {
			setBusinessInfo, toNextStep, accountCreated, slideToEstimate, receiveUser, signUpToken,
		} = this.props;

		setBusinessInfo(values);
		if (accountCreated) {
			this.setState({ loading : true });
			this.updateAddress(signUpToken, StreetAddress, City, StateId, ZipCode)
				.then(({ user, addressStatus }) => {
					if (addressStatus === 201) {
						receiveUser(user);
						this.setState({
							loading : false,
						}, slideToEstimate);
					}
				})
				.catch(() => {
					this.setState({ loading : false });
					utils.notify({
						type    : 'error',
						title   : 'Unable to add contact address',
						message : 'Please try again. If the issue persists, please contact support.',
						timeOut : 2500,
					});
				});
		} else {
			toNextStep();
		}
	}

	render() {
		const {
			USStates, toPrevStep, JobEventType, accountCreated, user,
		} = this.props;

		const { locationTypes, loadingLocationTypes, loading } = this.state;

		return (
			<div className="form-panel panel panel-default text-center">
				<div className="panel-heading text-center">
					<h4 className="panel-title text-primary">{ JobEventType }</h4>
				</div>
				<div className="panel-body container-fluid">
					<div className="row panel-row">
						<h4>What is Your Business Address?</h4>
					</div>
					<div>
						<Formik
							initialValues={this.initialValues}
							validationSchema={!user.Id ? validationSchema.shape(locationValidation) : validationSchema}
							validateOnChange={false}
							validateOnBlur={false}
							onSubmit={this.handleSubmit}
						>
							{({ errors, setFieldValue }) => (
								<Form id="marketplace-location-form">
									<If condition={!user.Id}>
										<div className="row">
											<div className="col-xs-12 form-group">
												<label htmlFor="LocationName" className="control-label">Business Name</label>
												<Field
													name="LocationName"
													id="LocationName"
													className="form-control"
													disabled={accountCreated}
												/>
												<small className="text-danger">{errors.LocationName}</small>
											</div>
										</div>
									</If>

									<div className="row">
										<div className="col-xs-12 form-group">
											<label htmlFor="LocationTypeId" className="control-label">Business Type</label>
											<Field
												name="LocationTypeId"
												disabled={accountCreated}
												render={({ field }) => (
													<Select
														// eslint-disable-next-line react/jsx-props-no-spreading
														{...field}
														id="LocationTypeId"
														className="react-select text-left"
														onChange={(option) => setFieldValue('LocationTypeId', option ? option.value : '')}
														options={locationTypes}
														isLoading={loadingLocationTypes}
														isClearable
														isSearchable
													/>
												)}
											/>
											<small className="text-danger">{errors.LocationTypeId}</small>
										</div>
									</div>

									<div className="row">
										<div className="col-xs-12 form-group">
											<label htmlFor="StreetAddress" className="control-label">Address</label>
											<Field
												name="StreetAddress"
												id="StreetAddress"
												className="form-control"
											/>
											<small className="text-danger">{errors.StreetAddress}</small>
										</div>
									</div>

									<div className="row">
										<div className="col-xs-12 form-group">
											<label htmlFor="City" className="control-label">City</label>
											<Field
												name="City"
												id="City"
												className="form-control"
											/>
											<small className="text-danger">{errors.City}</small>
										</div>
									</div>

									<div className="row">
										<div className="col-xs-12 col-sm-6 form-group">
											<label htmlFor="StateId" className="control-label">State</label>
											<Field
												as="select"
												name="StateId"
												id="StateId"
												className="form-control"
											>
												<option value="">Select...</option>
												<For
													each="item"
													of={USStates}
												>
													<option value={item.value} key={`marketplace_state_${item.value}`}>{item.label}</option>
												</For>
											</Field>
											<small className="text-danger">{errors.StateId}</small>
										</div>

										<div className="col-xs-12 col-sm-6 form-group">
											<label htmlFor="ZipCode" className="control-label">Zip</label>
											<Field
												name="ZipCode"
												id="ZipCode"
												className="form-control"
												maxLength="5"
											/>
											<small className="text-danger">{errors.ZipCode}</small>
										</div>
									</div>

									<div className="row marketplace-action-buttons-container">
										<button
											type="button"
											className="btn btn-link col-xs-4 col-sm-offset-2 col-xs-offset-1"
											onClick={toPrevStep}
											disabled={loading}
										>
											Back
										</button>

										<button
											type="submit"
											className="btn btn-primary col-xs-6 col-sm-4"
											disabled={loading}
										>
											{loading ? 'Please wait...' : 'Next'}
										</button>
									</div>
								</Form>
							)}
						</Formik>
					</div>
				</div>
			</div>

		);
	}
}

BusinessInfo.propTypes = {
	USStates        : PropTypes.array,
	JobEventType    : PropTypes.string,
	signUpToken     : PropTypes.string,
	toNextStep      : PropTypes.func.isRequired,
	toPrevStep      : PropTypes.func.isRequired,
	setBusinessInfo : PropTypes.func.isRequired,
	accountCreated  : PropTypes.bool.isRequired,
	slideToEstimate : PropTypes.func.isRequired,
	receiveUser     : PropTypes.func.isRequired,
	user            : PropTypes.object,
};

BusinessInfo.defaultProps = {
	USStates     : [],
	JobEventType : 'Location Form',
	signUpToken  : null,
	user         : {},
};

export default BusinessInfo;
